import { splitHierarchyPairId } from 'src/components/star-grid/services/filterHierarchyService';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { defaultScaleIdFor, PNL_SCALE_OPTIONS } from 'src/components/star-grid/utils/pnlGrid';
import { isOpeningView, reportQueryKnobs } from 'src/components/star-grid/utils/reportViewQuery';
import { queryNotices } from 'src/components/star-grid/utils/queryNotices';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { useLoadDuration } from 'src/components/star-grid/hooks/useLoadDuration';
import { useReportDeckTypes } from 'src/components/star-grid/hooks/useReportDeckTypes';
import { useReportDrill } from 'src/components/star-grid/hooks/useReportDrill';
import { useReportSpec } from 'src/components/star-grid/hooks/useReportSpec';
import { useReportVersionPicker } from 'src/components/star-grid/hooks/useReportVersionPicker';
import { useScreenViewControls } from 'src/components/star-grid/hooks/useScreenViewControls';
import { isVersionControl } from 'src/components/star-grid/utils/screenViewControls';
import { useShowBy } from 'src/components/star-grid/hooks/useShowBy';

/** Which load the badge times: the report's own query or the last drill. */
function useLastLoad(running: boolean, drilling: boolean): 'query' | 'drill' {
  const [lastLoad, setLastLoad] = useState<'query' | 'drill'>('query');
  useEffect(() => {
    if (running) setLastLoad('query');
    else if (drilling) setLastLoad('drill');
  }, [running, drilling]);
  return lastLoad;
}

/**
 * State behind the report viewer. Filter overrides, show-by, zero suppression, version and deck are
 * per-query knobs: they re-scope this session's view without editing the saved report.
 */
export function useReportView(reportId: number, text: StarGridText) {
  // The viewer draws rows from the query's own presentation, so it skips the editor's screen import.
  const report = useReportSpec(String(reportId), false, true);
  const { spec, run, takeOpeningQuery } = report;
  // Keyed by dimension; an empty list is kept because it asks the server to widen back to All.
  const [overrides, setOverrides] = useState<Record<string, readonly string[]>>({});
  // Until the user picks a scale, it follows the report: a headcount report opens in whole heads.
  const [chosenScaleId, setScaleId] = useState<string | null>(null);
  const scaleId = chosenScaleId ?? defaultScaleIdFor(spec?.cube_source);
  const [suppressZeroRows, setSuppressZeroRows] = useState(true);
  const version = useReportVersionPicker(spec?.id, spec?.view_controls?.find((control) => control.id === 'version')?.value);
  const ownDeck = useMemo(() => spec?.view_controls?.find((control) => control.id === 'deck')?.value, [spec?.view_controls]);
  // A control the screen makes selectable offers the screen's own list; the catalog pickers are fallbacks.
  // Version picks are the exception: every one -- the report's and a comparison's Version 1 / 2 -- lists
  // every Iceberg snapshot and tag, a superset of the screen's.
  const pickable = useMemo(() => spec?.view_controls?.filter((control) => !isVersionControl(control)), [spec?.view_controls]);
  const compareVersions = useMemo(
    () => spec?.view_controls?.filter((control) => control.id !== 'version' && isVersionControl(control)) ?? [],
    [spec?.view_controls]
  );
  const [versionPicks, setVersionPicks] = useState<Readonly<Record<string, string>>>({});
  const pickVersion = useCallback((controlId: string, value: string) => setVersionPicks((was) => ({ ...was, [controlId]: value })), []);
  const screenControls = useScreenViewControls(pickable);
  const screenDeck = screenControls.isSelectable('deck');
  // The deck catalog only backs the fallback picker; a screen that lists its decks needs no fetch.
  const deck = useReportDeckTypes(ownDeck, Boolean(spec) && !screenDeck);
  const asOf = version.asOf;
  const deckType = (screenDeck ? screenControls.knobs.deckType : deck.value) ?? '';
  const viewOverrides = useMemo(
    () => ({ ...screenControls.knobs.viewOverrides, ...versionPicks }),
    [screenControls.knobs.viewOverrides, versionPicks]
  );
  const showBy = useShowBy(report.grid?.show_by);
  // Keyed by the filter's (dimension, hierarchy) pair; a bare dimension (Filter by on a row) still works.
  const filterOverrides = useMemo(
    () =>
      Object.entries(overrides).map(([key, members]) => {
        const pair = splitHierarchyPairId(key);
        return pair ? { dimension: pair.dimension, hierarchy: pair.hierarchy, members: [...members] } : { dimension: key, members: [...members] };
      }),
    [overrides]
  );
  const queryKnobs = useMemo(
    () => reportQueryKnobs({ filterOverrides, showBy: showBy.requested, suppressZeroRows, asOf, deckType, viewOverrides }),
    [filterOverrides, showBy.requested, suppressZeroRows, asOf, deckType, viewOverrides]
  );
  // The drill menu belongs to the deck the grid is showing, so it follows `deck`; a drill breaks down
  // what the grid shows, so it takes the same knobs.
  const perDeckMenu = Object.keys(spec?.deck_overrides ?? {}).length > 0;
  const drill = useReportDrill(spec?.id, deckType || undefined, {
    rowAxis: spec?.row_axis,
    rows: report.grid?.rows,
    query: queryKnobs,
    dimensionsDeck: perDeckMenu ? deckType : ''
  });
  // Off the first paint, but ready before the first right-click.
  const { loadDimensions } = drill;
  const gridShown = Boolean(report.grid) && !report.running;
  useEffect(() => {
    if (gridShown) loadDimensions();
  }, [gridShown, loadDimensions]);
  const loadDurationMs = useLoadDuration(report.running || drill.busy);
  const lastLoad = useLastLoad(report.running, drill.busy);
  // Read by the stats panel when it is opened, not fetched on every load.
  const statsSource = useMemo(
    () => ({ reportId: spec?.id, queryId: report.running ? undefined : report.grid?.query_id, factTable: report.grid?.fact_table }),
    [spec?.id, report.running, report.grid?.query_id, report.grid?.fact_table]
  );

  const viewControlActions = useMemo(
    () => ({
      deck: { options: deck.options, onChange: deck.setValue, value: deck.value || ownDeck },
      scale: {
        options: PNL_SCALE_OPTIONS.map((option) => ({ value: option.id, label: text(option.labelKey) })),
        onChange: (value: string) => setScaleId(value),
        value: scaleId
      },
      ...screenControls.actions
    }),
    [screenControls.actions, text, scaleId, deck.options, deck.setValue, deck.value, ownDeck]
  );

  useEffect(() => {
    if (!spec) return;
    const opening = takeOpeningQuery?.();
    void (opening && isOpeningView(queryKnobs, ownDeck) ? run(queryKnobs, undefined, opening) : run(queryKnobs));
    // A new query shape invalidates the previous expansions.
    drill.reset();
  }, [spec, run, queryKnobs, takeOpeningQuery, ownDeck]);

  const notices = useMemo(() => queryNotices(report.grid), [report.grid]);

  const onFilterChange = useCallback(
    (dimension: string, members: readonly string[]) => setOverrides((was) => ({ ...was, [dimension]: members })),
    []
  );

  return {
    report,
    spec,
    version,
    drill,
    showBy,
    overrides,
    scaleId,
    suppressZeroRows,
    setSuppressZeroRows,
    viewControlActions,
    compareVersions,
    pickVersion,
    notices,
    onFilterChange,
    loadDurationMs,
    serverDurationMs: lastLoad === 'drill' ? drill.serverMs : report.grid?.timings_ms?.total_ms,
    queries: lastLoad === 'drill' ? drill.queries : report.grid?.queries,
    explain: report.explain,
    statsSource,
    busy: report.running || drill.busy
  };
}
